import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Users, Activity, MessageSquare, CheckCircle, TrendingUp, AlertTriangle, Globe, Chrome, Database, Store, Layers, Play, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LAYER_ICONS = { pi_check: '🛡', pi_craft: '✏', token_optimizer: '⚡', pi_cast: '🎯', pi_loop: '🔁', pi_store: '🏪' };

function SeedDatabasePanel() {
  const [status, setStatus] = useState(null);
  const [seeding, setSeeding] = useState({ casts: false, packages: false });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/seed-status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error('Seed status error:', e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const triggerSeedCasts = async (force = false) => {
    setSeeding(s => ({ ...s, casts: true }));
    try {
      const res = await fetch(`${API}/api/admin/seed-casts`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ force }) });
      const data = await res.json();
      toast.success(data.message || 'Seed started');
      fetchStatus();
    } catch (e) { toast.error('Failed to start seeding'); }
    finally { setSeeding(s => ({ ...s, casts: false })); }
  };

  const triggerSeedPackages = async () => {
    setSeeding(s => ({ ...s, packages: true }));
    try {
      const res = await fetch(`${API}/api/admin/seed-packages`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      const data = await res.json();
      toast.success(data.message || 'Package seed started');
      fetchStatus();
    } catch (e) { toast.error('Failed to start seeding'); }
    finally { setSeeding(s => ({ ...s, packages: false })); }
  };

  if (!status) return null;

  const castCount = status.current_count || 0;
  const pkgCount = status.package_count || 0;
  const seedStatus = status.status?.status || 'unknown';
  const isRunning = seedStatus === 'running';
  const isFailed = seedStatus === 'failed';
  const castsNeeded = castCount < 4000000;
  const pkgsNeeded = pkgCount < 75;
  const rate = status.status?.rate_per_sec || 0;

  if (!castsNeeded && !pkgsNeeded) return null;

  return (
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5" data-testid="seed-panel">
      <div className="flex items-center gap-3 mb-3">
        <Database className="w-5 h-5 text-amber-400" />
        <h2 className="text-white font-bold text-sm">Database Seeding Required</h2>
        <button onClick={fetchStatus} className="ml-auto text-white/30 hover:text-white"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      <p className="text-white/40 text-xs mb-4">The PI Store needs cast data to function. This runs in the background and takes ~4-5 minutes.</p>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div className="bg-black/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/50 text-xs">Precision Casts</span>
            <span className={`text-xs font-bold ${castCount >= 4000000 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {castCount.toLocaleString()} / 4,320,000
            </span>
          </div>
          {castCount < 4000000 && (
            <div className="mb-2">
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${Math.min((castCount / 4320000) * 100, 100)}%` }} />
              </div>
            </div>
          )}
          <button
            onClick={() => triggerSeedCasts(false)}
            disabled={seeding.casts || isRunning || castCount >= 4000000}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-300 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="seed-casts-btn"
          >
            {isRunning ? <><RefreshCw className="w-3 h-3 animate-spin" /> Seeding {rate ? `(${rate}/s)` : '...'}</> :
             castCount >= 4000000 ? <><CheckCircle className="w-3 h-3" /> Complete</> :
             isFailed ? <><Play className="w-3 h-3" /> Retry Seed</> :
             <><Play className="w-3 h-3" /> Seed 4.3M Casts</>}
          </button>
          {(isRunning || isFailed) && castCount > 0 && castCount < 4000000 && (
            <button
              onClick={() => triggerSeedCasts(true)}
              disabled={seeding.casts}
              className="w-full flex items-center justify-center gap-2 px-2 py-1.5 mt-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400/70 text-[10px] font-medium hover:bg-red-500/20"
              data-testid="force-reseed-btn"
            >
              <RefreshCw className="w-2.5 h-2.5" /> Force Resume (skips seeded domains)
            </button>
          )}
        </div>

        <div className="bg-black/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/50 text-xs">Store Packages</span>
            <span className={`text-xs font-bold ${pkgCount >= 75 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {pkgCount} / 81
            </span>
          </div>
          {pkgCount < 75 && (
            <div className="mb-2">
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${Math.min((pkgCount / 81) * 100, 100)}%` }} />
              </div>
            </div>
          )}
          <button
            onClick={triggerSeedPackages}
            disabled={seeding.packages || pkgCount >= 75}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-300 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="seed-packages-btn"
          >
            {pkgCount >= 75 ? <><CheckCircle className="w-3 h-3" /> Complete</> :
             <><Play className="w-3 h-3" /> Seed 81 Packages</>}
          </button>
        </div>
      </div>

      {isRunning && <p className="text-amber-400/60 text-[10px] text-center">Seeding in progress ({rate ? `${rate}/s` : '...'}) — auto-refreshes every 5s. Resumable if interrupted.</p>}
      {isFailed && <p className="text-red-400/60 text-[10px] text-center">Seed failed: {status.status?.error || 'unknown'}. Click "Force Resume" to continue from where it stopped.</p>}
    </div>
  );
}

function UsageHeatmap() {
  const [heatmap, setHeatmap] = useState(null);
  const fetchHeatmap = useCallback(() => {
    fetch(`${API}/api/admin/heatmap`, { credentials: 'include' }).then(r => r.json()).then(setHeatmap).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHeatmap(); }, [fetchHeatmap]);
  if (!heatmap) return null;
  const { heatmap: grid, day_names, max_value } = heatmap;
  const getColor = (val) => {
    if (val === 0) return 'bg-white/5';
    const intensity = val / Math.max(max_value, 1);
    if (intensity > 0.75) return 'bg-cyan-400';
    if (intensity > 0.5) return 'bg-cyan-500/70';
    if (intensity > 0.25) return 'bg-cyan-600/50';
    return 'bg-cyan-700/30';
  };
  return (
    <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="usage-heatmap">
      <h2 className="text-base font-semibold text-white mb-4">Usage Heatmap (Last 30 Days)</h2>
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="flex mb-1 pl-16">{Array.from({ length: 24 }, (_, i) => (<div key={`hour-${i}`} className="flex-1 text-center text-[10px] text-white/30">{i % 3 === 0 ? `${i}` : ''}</div>))}</div>
          {grid.map((row, dayIdx) => (
            <div key={day_names[dayIdx]} className="flex items-center mb-0.5">
              <span className="w-16 text-xs text-white/40 pr-2 text-right shrink-0">{day_names[dayIdx].slice(0, 3)}</span>
              <div className="flex flex-1 gap-0.5">
                {row.map((val, hourIdx) => (<div key={hourIdx} className={`flex-1 h-5 rounded-sm ${getColor(val)} group relative`} title={`${day_names[dayIdx]} ${hourIdx}:00 — ${val} events`} />))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const { stats, refreshStats } = useOutletContext();
  const [retention, setRetention] = useState(null);

  const fetchRetention = useCallback(async () => {
    try { const res = await fetch(`${API}/api/admin/retention`, { credentials: 'include' }); if (res.ok) setRetention(await res.json()); } catch (e) { console.error("Retention fetch error:", e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRetention(); const interval = setInterval(refreshStats, 60000); return () => clearInterval(interval); }, [fetchRetention, refreshStats]);

  if (!stats) return <div className="text-white/50">Loading dashboard...</div>;

  const lc = stats.layer_counts || {};
  const lct = stats.layer_counts_today || {};
  const sc = stats.source_counts || {};
  const store = stats.store || {};

  const topMetrics = [
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-400' },
    { label: 'Active Today', value: stats.active_today, icon: Activity, color: 'text-green-400' },
    { label: 'Active Week', value: stats.active_week, icon: TrendingUp, color: 'text-cyan-400' },
    { label: 'Total Events', value: lc.total || 0, icon: Layers, color: 'text-violet-400' },
    { label: 'Events Today', value: lct.total || 0, icon: Activity, color: 'text-amber-400' },
    { label: 'Consent Signed', value: stats.consent_signed, icon: CheckCircle, color: 'text-emerald-400' },
    { label: 'Open Tickets', value: stats.open_tickets, icon: MessageSquare, color: 'text-orange-400' },
    { label: 'Deletions', value: stats.deletion_requests, icon: AlertTriangle, color: 'text-red-400' },
  ];

  const layerCards = [
    { key: 'pi_check', name: 'PI Check', color: 'from-violet-500/20 to-violet-600/5' },
    { key: 'pi_craft', name: 'PI Craft', color: 'from-cyan-500/20 to-cyan-600/5' },
    { key: 'token_optimizer', name: 'Token Optimizer', color: 'from-emerald-500/20 to-emerald-600/5' },
    { key: 'pi_cast', name: 'PI Cast', color: 'from-amber-500/20 to-amber-600/5' },
    { key: 'pi_loop', name: 'PI Loop', color: 'from-pink-500/20 to-pink-600/5' },
    { key: 'pi_store', name: 'PI Store', color: 'from-blue-500/20 to-blue-600/5' },
  ];

  return (
    <div data-testid="admin-overview" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pilot Dashboard</h1>
        <p className="text-white/40 mt-1 text-sm">Real-time overview — updates every 60s</p>
      </div>

      {/* Seed Database Panel — only shows when data is missing */}
      <SeedDatabasePanel />

      {stats.suspicious_ips?.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-300 text-sm">{stats.suspicious_ips.length} suspicious IP(s) detected</span>
        </div>
      )}

      {/* Top metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {topMetrics.map(m => (
          <div key={m.label} className="bg-[#0E1830] border border-white/10 rounded-xl p-4">
            <m.icon className={`w-4 h-4 ${m.color} mb-2`} />
            <div className="text-xl font-bold text-white">{(m.value || 0).toLocaleString()}</div>
            <div className="text-white/40 text-xs mt-0.5">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Layer breakdown */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
        <h2 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-400" /> Usage by PI Layer</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {layerCards.map(l => (
            <div key={l.key} className={`bg-gradient-to-br ${l.color} border border-white/[0.06] rounded-lg p-3`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/60 text-xs font-medium">{l.name}</span>
                <span className="text-[10px] text-white/30">today: {lct[l.key] || 0}</span>
              </div>
              <p className="text-white font-bold text-lg">{(lc[l.key] || 0).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Source + Store row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" /> Source</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Web App</span><span className="text-white font-bold text-sm">{sc.web || 0}</span></div>
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Chrome Extension</span><span className="text-white font-bold text-sm">{sc.chrome_extension || 0}</span></div>
          </div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Store className="w-4 h-4 text-blue-400" /> PI Store</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Packages</span><span className="text-white font-bold text-sm">{store.total_packages || 0}</span></div>
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Seeded Casts</span><span className="text-white font-bold text-sm">{(store.total_casts_seeded || 0).toLocaleString()}</span></div>
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">User Casts</span><span className="text-white font-bold text-sm">{store.user_casts_created || 0}</span></div>
          </div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-green-400" /> Segments</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Google Users</span><span className="text-white font-bold text-sm">{stats.google_users || 0}</span></div>
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Extension Users</span><span className="text-white font-bold text-sm">{stats.extension_users || 0}</span></div>
            <div className="flex justify-between items-center"><span className="text-white/50 text-xs">Active Chats</span><span className="text-white font-bold text-sm">{stats.active_chats || 0}</span></div>
          </div>
        </div>
      </div>

      {/* Retention */}
      {retention && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-base font-semibold text-white mb-4">Retention</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(retention).map(([key, val]) => (
              <div key={key} className="text-center">
                <div className={`text-3xl font-bold ${val.percentage >= 30 ? 'text-green-400' : val.percentage >= 15 ? 'text-amber-400' : 'text-red-400'}`}>{val.percentage}%</div>
                <div className="text-white/40 text-sm mt-1">{key.replace('_', ' ')} retention</div>
                <div className="text-white/30 text-xs">{val.retained}/{val.eligible}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5"><div className="text-white/40 text-sm">Recent Signups (7d)</div><div className="text-2xl font-bold text-white mt-1">{stats.recent_signups}</div></div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5"><div className="text-white/40 text-sm">Brain Coach Waitlist</div><div className="text-2xl font-bold text-white mt-1">{stats.waitlist}</div></div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5"><div className="text-white/40 text-sm">Feedback Responses</div><div className="text-2xl font-bold text-white mt-1">{stats.feedback_count}</div></div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5"><div className="text-white/40 text-sm">MCP API Calls Today</div><div className="text-2xl font-bold text-white mt-1">{stats.mcp?.total_calls_today || 0}</div></div>
      </div>

      {/* MCP Analytics */}
      {stats.mcp && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-400" /> MCP & API</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{stats.mcp.total_keys}</p><p className="text-white/30 text-xs">Active Keys</p></div>
            <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{stats.mcp.unique_users_with_keys}</p><p className="text-white/30 text-xs">Users with Keys</p></div>
            <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{stats.mcp.total_calls_today}</p><p className="text-white/30 text-xs">Calls Today</p></div>
            <div className="bg-white/[0.03] rounded-lg p-3"><p className="text-white font-bold text-lg">{(stats.mcp.total_calls_all || 0).toLocaleString()}</p><p className="text-white/30 text-xs">Total Calls</p></div>
          </div>
        </div>
      )}

      <UsageHeatmap />
    </div>
  );
}
